import {
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
  NormalizedCacheObject,
  ServerError,
  ServerParseError,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import * as sentry from '@sentry/browser'

import BuildConfig from './buildConfig'
import { graphqlPath, graphqlUnauthPath, getApiUrl } from './consts/urlConsts'
import { getExtensionInstallationId } from './metrics/idGetSet'
import { refreshAccessToken } from './serverClient'
import {
  getHasValidAccessToken,
  getHasValidRefreshToken,
  getJwtCsrfHeader,
} from './utils/apiAuth'
import * as canIUse from './utils/canIUse'
import { INSTALLATION_ID_HEADER_NAME } from './utils/enums'
import { logError } from './utils/loggingUtils'
import {
  getCachedParentContext,
  getBackgroundParentContext,
} from './utils/tracing'

const cache = new InMemoryCache()

export const isServerError = (
  error: Error | ServerParseError | ServerError | null,
): error is ServerError =>
  error !== null && (error as ServerError).statusCode !== undefined

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (
    networkError &&
    // @ts-ignore:next-line  (avoid annoying union type error)
    networkError?.statusCode !== 403 &&
    // @ts-ignore:next-line
    networkError?.statusCode !== 401
  ) {
    sentry.setTag('gqlOperationName', operation?.operationName)

    // Add additional information from graphQLErrors sentry, if available
    let errorMessage = networkError.message
    if (graphQLErrors && graphQLErrors.length > 0) {
      sentry.setTag('gqlErrors', graphQLErrors.map((x) => x.message).join(', '))
      errorMessage = `${errorMessage} - ${graphQLErrors[0].message}`
    }

    logError(new Error(errorMessage), 'apollo-client')
  }
})

const stagingHeaderLink = setContext(async (_operation, prevContext) => {
  let updatedHeaders: Record<string, string> = {
    ...(prevContext?.headers || {}),
  }

  if (BuildConfig.STAGING_BASIC_AUTH) {
    updatedHeaders = {
      ...updatedHeaders,
      Authorization: `Basic ${btoa(BuildConfig.STAGING_BASIC_AUTH)}`,
    }
  }

  return { headers: updatedHeaders }
})

const installationIdHeaderLink = setContext(async (_operation, prevContext) => {
  const installationId = await getExtensionInstallationId()
  const updatedHeaders: Record<string, string> = {
    ...(prevContext?.headers || {}),
  }

  if (installationId) {
    updatedHeaders[INSTALLATION_ID_HEADER_NAME] = installationId
  }

  return { headers: updatedHeaders }
})

const contentScriptTracingLink = setContext(async (_operation, prevContext) => {
  const cachedCarrier = getCachedParentContext()

  if (!cachedCarrier) {
    return { headers: prevContext?.headers || {} }
  }

  return {
    headers: {
      ...(prevContext?.headers || {}),
      ...cachedCarrier,
    },
  }
})

const backgroundScriptTracingLink = setContext(
  async (_operation, prevContext) => {
    const carrier = await getBackgroundParentContext()

    if (!carrier) {
      return { headers: prevContext?.headers || {} }
    }

    return {
      headers: {
        ...(prevContext?.headers || {}),
        ...carrier,
      },
    }
  },
)

const tracingLink = canIUse.chromeBackground()
  ? backgroundScriptTracingLink
  : contentScriptTracingLink

const refreshTokenLink = setContext(async () => {
  const hasValidAccessToken = await getHasValidAccessToken()
  const hasValidRefreshToken = await getHasValidRefreshToken()

  if (!hasValidAccessToken && hasValidRefreshToken) {
    await refreshAccessToken()
  }
})

function getAuthLink() {
  const authHttpLink = createHttpLink({
    uri: getApiUrl(graphqlPath, null),
  })

  const authHeaderLink = setContext(async (_operation, prevContext) => {
    let updatedHeaders = {
      ...(prevContext?.headers || {}),
    }

    const [name, value] = await getJwtCsrfHeader('access')
    if (name && value) {
      updatedHeaders = {
        ...updatedHeaders,
        [name]: value,
      }
    }

    return { headers: updatedHeaders }
  })

  return from([
    refreshTokenLink,
    installationIdHeaderLink,
    stagingHeaderLink,
    tracingLink,
    authHeaderLink,
    errorLink,
    authHttpLink,
  ])
}

function getUnauthLink() {
  const unauthHttpLink = createHttpLink({
    uri: getApiUrl(graphqlUnauthPath, null),
  })

  return from([
    installationIdHeaderLink,
    stagingHeaderLink,
    tracingLink,
    errorLink,
    unauthHttpLink,
  ])
}

let client: ApolloClient<NormalizedCacheObject> | undefined

export function getClient() {
  if (client) {
    return client
  }

  client = new ApolloClient({
    cache,
    // In our case, we always want updated data, so we default to no-cache.
    // This means "fetch from server, and don't even bother to update the cache".
    defaultOptions: { query: { fetchPolicy: 'no-cache' } },
    link: getAuthLink(),
  })

  return client
}

let unauthClient: ApolloClient<NormalizedCacheObject> | undefined

export function getUnauthClient() {
  if (unauthClient) {
    return unauthClient
  }

  unauthClient = new ApolloClient({
    cache,
    // In our case, we always want updated data, so we default to no-cache.
    // This means "fetch from server, and don't even bother to update the cache".
    defaultOptions: { query: { fetchPolicy: 'no-cache' } },
    link: getUnauthLink(),
  })

  return unauthClient
}
